import { useEffect } from 'react';
import { useInViewport } from '@acorn-portal/shared/src/components/telematics/common/use-in-viewport';

/**
 * Options for {@link useLoadMoreOnScroll}.
 */
export interface UseLoadMoreOnScrollOptions {
	/**
	 * Loads the next page. Wrap in `useCallback` and include the current page number
	 * in its dependencies: a new identity after each load is what lets the hook keep
	 * paging while the sentinel remains visible on tall screens.
	 */
	onLoadMore: () => void;
	/** False while a page is loading, after an error, or when there are no more pages. */
	enabled: boolean;
	/** How far below the viewport to start loading. Default: '400px'. */
	rootMargin?: string;
}

/**
 * Infinite scroll driver. Attach the returned ref to a sentinel element placed
 * after the list. `onLoadMore` runs whenever the sentinel is in view and
 * `enabled` is true, including immediately after a page finishes loading if the
 * sentinel is still on screen.
 *
 * @example
 * ```tsx
 * const sentinelRef = useLoadMoreOnScroll({ onLoadMore: loadNextPage, enabled: hasNextPage && !loading });
 * return <>{items}<div ref={sentinelRef} /></>;
 * ```
 */
export const useLoadMoreOnScroll = ({ onLoadMore, enabled, rootMargin = '400px' }: UseLoadMoreOnScrollOptions) => {
	const { ref, isInViewport } = useInViewport({ rootMargin });

	useEffect(() => {
		if (enabled && isInViewport) onLoadMore();
	}, [enabled, isInViewport, onLoadMore]);

	return ref;
};

export default useLoadMoreOnScroll;
